{
  "nav": {
    "home": "Home",
    "documents": "Documents",
    "properties": "Properties",
    "payments": "Payments",
    "budget": "Budget",
    "tasks": "Tasks",
    "investments": "Investments",
    "finances": "Finances",
    "settings": "Settings",
    "configure": "Configuration",
    "logout": "Log out",
    "closeMenu": "Close menu",
    "collapseSidebar": "Collapse sidebar",
    "expandSidebar": "Expand sidebar",
    "documentQueue": "Document Queue",
    "family": "Family",
    "career": "Career",
    "tools": "Tools",
    "aiChat": "AI Chat",
    "knowledge": "Knowledge",
    "bundles": "Bundles",
    "utilityMigrations": "Utility migrations",
    "hoaCharges": "HOA charges",
    "estatePlanner": "Estate planner",
    "ghostfolio": "Ghostfolio",
    "appName": "FamilyHub",
    "restrictedAccess": "restricted access"
  },
  "errors": {
    "forbidden": "You don't have permission for this action.",
    "careerReadOnly": "This profile is read-only: you can view it but not change it.",
    "notFound": "Resource not found.",
    "server": "Server error. Please try again.",
    "connection": "Connection error. Check your internet.",
    "noAccessTitle": "No access",
    "noAccessDescription": "You don't have permission to view this page. Ask an administrator if you need access."
  },
  "aiChat": {
    "title": "AI Chat",
    "checking": "Checking AI status...",
    "ready": "AI is ready",
    "open": "Open AI Chat",
    "unavailable": "AI service unavailable",
    "unavailableHint": "The AI engine could not start within 30 seconds. It may be busy or offline.",
    "retry": "Retry",
    "openAnyway": "Open WebUI ↗",
    "starting": "Starting AI engine... This may take a few minutes on first load.",
    "startingHint": "The AI model is loading into GPU memory. You'll be redirected automatically once it's ready."
  },
  "bundles": {
    "sidebarHint": "Group related documents into bundles for easy organization",
    "filterPlaceholder": "Filter bundles…",
    "empty": "No bundles yet.",
    "createFirst": "Create your first bundle"
  },
  "auth": {
    "login": "Login",
    "logout": "Logout",
    "username": "Username",
    "password": "Password",
    "welcome": "Welcome!",
    "loginTitle": "Sign in",
    "loginSubtitle": "Enter your FamilyHub account",
    "loginButton": "Sign In",
    "loggingIn": "Signing in...",
    "usernameRequired": "Username is required",
    "passwordRequired": "Password is required",
    "invalidCredentials": "Invalid username or password",
    "showPassword": "Show password",
    "hidePassword": "Hide password",
    "tagline": "Family management, simplified",
    "copyright": "© 2024 FamilyHub. All rights reserved.",
    "wakingUp": "Backend is waking up…",
    "backendUnavailableTitle": "Backend unavailable",
    "backendUnavailableMessage": "We could not reach the server. Your session is still saved — try again in a moment.",
    "backendUnavailableRetry": "Retry",
    "throttled": "Too many attempts. Wait a few minutes and try again."
  },
  "login": {
    "title": "Sign in",
    "subtitle": "Enter your FamilyHub account",
    "email": "Email",
    "password": "Password",
    "submit": "Sign in",
    "loading": "Signing in...",
    "welcome": "Welcome back!",
    "invalidEmail": "Invalid email address",
    "passwordRequired": "Password is required",
    "wrongCredentials": "Incorrect email or password",
    "securityCheckFailed": "Security check failed. Reload the page and try again.",
    "serviceStarting": "The service is starting. Please wait a few minutes, then try signing in again."
  },
  "passwordReset": {
    "forgotLink": "Forgot password?",
    "forgotTitle": "Reset password",
    "forgotSubtitle": "Enter your email address and we will send you a reset link.",
    "submit": "Send reset link",
    "sending": "Sending...",
    "confirmation": "If an account exists for this email, you will receive a reset link.",
    "backToLogin": "Back to sign in",
    "resetTitle": "New password",
    "resetSubtitle": "Choose a new password for your account.",
    "newPassword": "New password",
    "confirmPassword": "Confirm password",
    "passwordsMismatch": "Passwords do not match",
    "minLength": "Password must be at least {{count}} characters",
    "minLengthHint": "At least {{count}} characters",
    "submitReset": "Save password",
    "resetting": "Saving...",
    "successTitle": "Password updated",
    "successMessage": "Your password has been changed. You can sign in with your new password.",
    "invalidTokenTitle": "Link expired",
    "invalidTokenMessage": "This reset link is invalid or has expired.",
    "tokenMissing": "This reset link is incomplete. Request a new one.",
    "requestNewLink": "Request a new link",
    "unavailable": "Password reset is not available right now. Contact your administrator.",
    "confirmError": "We could not reset your password. Try again or request a new link."
  },
  "settings": {
    "title": "Settings",
    "subtitle": "Manage your account and preferences",
    "tabs": {
      "profile": "Profile",
      "security": "Security",
      "apiAccess": "API Access",
      "notifications": "Notifications",
      "appearance": "Appearance",
      "integrations": "Integrations",
      "users": "Users",
      "backup": "Backup",
      "accessRequests": "Access requests"
    },
    "profile": {
      "title": "Profile information",
      "description": "Update your personal information",
      "firstName": "First name",
      "lastName": "Last name",
      "email": "Email",
      "emailVerified": "Email verified",
      "emailNotVerified": "Email not verified",
      "phone": "Phone",
      "username": "Username",
      "usernamePlaceholder": "Optional login username",
      "save": "Save",
      "saved": "Profile updated",
      "error": "Error updating profile",
      "changePhoto": "Change photo",
      "removePhoto": "Remove",
      "avatarUploaded": "Profile picture updated",
      "avatarRemoved": "Profile picture removed",
      "avatarError": "Failed to update picture",
      "avatarInvalidType": "Only JPEG, PNG, WebP, and GIF files are allowed",
      "avatarTooLarge": "File must be under 5MB"
    },
    "security": {
      "title": "Change password",
      "description": "Make sure you use a strong password",
      "currentPassword": "Current password",
      "newPassword": "New password",
      "confirmPassword": "Confirm new password",
      "submit": "Change password",
      "mismatch": "Passwords do not match",
      "success": "Password changed successfully",
      "error": "Error changing password"
    },
    "notifications": {
      "title": "Notification preferences",
      "description": "Choose how you want to receive notifications",
      "emailPayments": "Email notifications for payments",
      "emailPaymentsDesc": "Receive email when a payment is registered or overdue",
      "emailReminders": "Email notifications for reminders",
      "emailRemindersDesc": "Receive email for scheduled reminders",
      "emailDocuments": "Email notifications for documents",
      "emailDocumentsDesc": "Receive email when a new document is added",
      "pushPayments": "Push notifications",
      "pushPaymentsDesc": "Receive push notifications in the browser",
      "telegram": "Telegram",
      "telegramDesc": "Alerts via @FamilyHubAssistantManagerBot"
    },
    "appearance": {
      "title": "Appearance & Language",
      "description": "Customize your experience",
      "theme": "Theme",
      "themeLight": "Light",
      "themeDark": "Dark",
      "themeSystem": "System",
      "language": "Language",
      "dateFormat": "Date format",
      "timezone": "Timezone",
      "save": "Save",
      "saved": "Appearance settings saved",
      "error": "Couldn't save your language preference"
    },
    "integrations": {
      "title": "External integrations",
      "description": "Connect external services for additional functionality",
      "connected": "Connected",
      "connect": "Connect",
      "disconnect": "Disconnect",
      "googleDrive": "Sync documents with Google Drive",
      "email": "Configure email sending"
    },
    "users": {
      "title": "User management",
      "description": "Manage users and permissions",
      "noPermission": "You don't have permission to manage users"
    },
    "apiAccess": {
      "title": "API keys",
      "description": "Create and manage API keys for OpenWebUI, MCP clients, and ChatGPT Actions",
      "createKey": "Create API key",
      "empty": "No API keys yet. Create one to connect external tools.",
      "revoke": "Revoke",
      "revoked": "API key revoked",
      "revokeError": "Failed to revoke API key",
      "editKey": "Edit API key scopes",
      "editScopes": "Edit scopes",
      "saveScopes": "Save scopes",
      "updated": "API key scopes updated",
      "updateError": "Failed to update API key scopes",
      "createError": "Failed to create API key",
      "confirmRevoke": "Revoke API key \"{{name}}\"? This cannot be undone.",
      "neverUsed": "Never",
      "nameRequired": "Key name is required",
      "serviceAdminOnly": "Only admins can create service keys",
      "namePlaceholder": "e.g. OpenWebUI assistant",
      "expiryHint": "Leave empty for no expiration",
      "permissionMode": "Permission setup",
      "usePreset": "Use a preset",
      "customPermissions": "Custom permissions",
      "selectPreset": "Select a preset…",
      "allowAll": "Full access (admin only)",
      "permissionTree": "Permissions by category",
      "catalogEmpty": "Permission catalog not available yet.",
      "mcpTools": "MCP tools",
      "mcpToolsEmpty": "No MCP tools in catalog.",
      "oneTimeWarning": "Copy this key now — it will not be shown again.",
      "yourKey": "API key",
      "copied": "Copied to clipboard",
      "copySnippet": "Copy",
      "done": "Done",
      "back": "Back",
      "next": "Next",
      "keyTypeUserHint": "Acts as the selected user; permissions are capped by their role.",
      "keyTypeServiceHint": "Standalone automation key with its own permission allowlist.",
      "columns": {
        "name": "Name",
        "type": "Type",
        "prefix": "Prefix",
        "scopes": "Scopes",
        "lastUsed": "Last used"
      },
      "keyTypes": {
        "user": "User",
        "service": "Service"
      },
      "fields": {
        "keyType": "Key type",
        "name": "Name",
        "owner": "Owner user",
        "expiry": "Expires on",
        "preset": "Preset"
      },
      "access": {
        "read": "Read",
        "write": "Write"
      },
      "wizard": {
        "step3Title": "Your new API key",
        "stepOf": "Step {{step}} of {{total}}"
      },
      "snippets": {
        "openwebui": "OpenWebUI environment",
        "mcp": "MCP config (Cursor / Claude Desktop)",
        "chatgpt": "ChatGPT Actions / Remote MCP"
      }
    }
  },
  "notifications": {
    "empty": "You're all caught up.",
    "markAllRead": "Mark all read",
    "unreadCount_zero": "No unread notifications",
    "unreadCount_one": "1 unread notification",
    "unreadCount_other": "{{count}} unread notifications"
  },
  "common": {
    "loading": "Loading...",
    "error": "Error",
    "success": "Success",
    "cancel": "Cancel",
    "delete": "Delete",
    "edit": "Edit",
    "add": "Add",
    "search": "Search",
    "filter": "Filter",
    "actions": "Actions",
    "notifications": "Notifications",
    "close": "Close",
    "allCategories": "All categories",
    "selectPeriod": "Select period",
    "selectBank": "Select bank",
    "configuration": "Configuration",
    "userMenu": "User menu",
    "openMenu": "Open menu",
    "logout": "Logout",
    "save": "Save",
    "saving": "Saving...",
    "all": "All",
    "assign": "Assign",
    "create": "Create",
    "import": "Import",
    "move": "Move",
    "overdue": "Overdue",
    "properties": "Properties",
    "remove": "Remove",
    "unknown": "Unknown",
    "yes": "Yes",
    "loadError": "Failed to load data. Please try again.",
    "retry": "Retry"
  },
  "accessRequests": {
    "noAccessMessage": "You do not have access to this section.",
    "adminOnlyNote": "Only a household administrator can enable this. Contact your admin if you need access.",
    "requestButton": "Request access",
    "pendingLabel": "Request pending",
    "cancelRequest": "Cancel request",
    "cancelRequestAria": "Cancel access request",
    "approve": "Approve",
    "deny": "Deny",
    "approveAria": "Approve request {{id}}",
    "denyAria": "Deny request {{id}}",
    "modules": {
      "apartments": "Apartments",
      "payments": "Payments",
      "budget": "Budget",
      "documents": "Documents",
      "tasks": "Tasks",
      "reminders": "Reminders",
      "knowledge": "Knowledge",
      "family": "Family",
      "ai_chat": "AI assistant",
      "integrations": "Integrations"
    },
    "target": {
      "building": "Building #{{id}}"
    },
    "status": {
      "pending": "Pending",
      "approved": "Approved",
      "denied": "Denied",
      "cancelled": "Cancelled"
    },
    "dialog": {
      "title": "Request access",
      "description": "Send a request to your household admin. You can add an optional message.",
      "messageLabel": "Message (optional)",
      "messagePlaceholder": "Why do you need access?",
      "messageHint": "Maximum 500 characters.",
      "requiresNote": "If approved, you will also receive: {{grants}}."
    },
    "requiredGrants": {
      "apartments_view_all": "read-only access to apartments"
    },
    "toast": {
      "created": "Access request sent.",
      "throttled": "Too many requests. Please try again later.",
      "createError": "Could not send the access request.",
      "duplicatePending": "A pending request already exists for this section.",
      "notPending": "This request is no longer pending (it was already handled).",
      "familyCannotDecide": "Only a household administrator can approve or deny access requests.",
      "cancelled": "Request cancelled.",
      "cancelError": "Could not cancel the request.",
      "approved": "Request approved.",
      "denied": "Request denied.",
      "decideError": "Could not update the request."
    },
    "settings": {
      "pendingTitle": "Pending access requests",
      "pendingDescription": "Approve or deny requests from viewers in your household.",
      "noPending": "No pending requests.",
      "mineTitle": "My requests",
      "mineDescription": "Status of access requests you submitted.",
      "noMine": "You have not submitted any requests yet.",
      "pendingBadge": "{{count}} pending access requests"
    }
  },
  "dashboard": {
    "title": "Dashboard",
    "subtitle": "Overview of your family's activities",
    "greeting": "Good day! 👋",
    "greetingName": "Good day, {{name}}! 👋",
    "summary": "Here's what's happening with your family today",
    "totalDocuments": "Total documents",
    "activeContracts": "Active contracts",
    "pendingPayments": "Pending payments",
    "overduePayments": "Overdue payments",
    "recentActivity": "Recent activity",
    "upcomingPayments": "Upcoming payments",
    "upcomingTasks": "Upcoming Tasks",
    "quickStats": "Quick Stats",
    "currentBudget": "Current month budget",
    "income": "Income",
    "expenses": "Expenses",
    "balance": "Balance",
    "noRecentActivity": "No recent activity yet",
    "noUpcomingPayments": "No upcoming payments",
    "apartments": {
      "total": "Total apartments",
      "occupied": "Occupied",
      "vacant": "Vacant",
      "overdue": "Overdue rent"
    },
    "activities": {
      "documentAdded": "New document added",
      "paymentRecorded": "Payment recorded",
      "taskCompleted": "Task completed"
    }
  },
  "budget": {
    "title": "Budget",
    "subtitle": "Track family income and expenses",
    "importCSV": "Import CSV",
    "addTransaction": "Add Transaction",
    "recentTransactions": "Recent Transactions",
    "noTransactions": "No transactions found",
    "importFromCSV": "Import from CSV",
    "importTransactionsCSV": "Import transactions from CSV",
    "bank": "Bank",
    "csvFile": "CSV File",
    "lastWeek": "Last week",
    "currentMonth": "Current month",
    "last3Months": "Last 3 months",
    "currentYear": "Current year",
    "comparedToLastMonth": "compared to last month",
    "uncategorized": "Uncategorized",
    "importSuccess": "Successfully imported {{count}} transactions",
    "importError": "Error importing transactions",
    "selectBank": "Select bank",
    "importing": "Importing...",
    "import": "Import",
    "incomeThisMonth": "Income this month",
    "expensesThisMonth": "Expenses this month",
    "totalAccountBalance": "Total account balance",
    "searchPlaceholder": "Search transactions...",
    "filterByType": "Filter by type",
    "allTypes": "All types",
    "income": "Income",
    "expenses": "Expenses",
    "filterByCategory": "Filter by category",
    "allCategories": "All categories",
    "noTransactionsFound": "No transactions found",
    "accounts": "Accounts",
    "bonds": {
      "annualLumpSum": "Annual lump sum",
      "fidelisTitle": "February — Fidelis (R3202A)",
      "netYr": "Net/yr:",
      "tbiScheduleTitle": "TBI Deposits — Maturity Schedule",
      "tezaurTitle": "November — Tezaur bonds"
    },
    "fiveYear": {
      "allFiguresNote": "All figures in RON · Appreciation: {{pct}}%/yr",
      "appreciation": "Appreciation",
      "base": "Base (10 mo)",
      "best": "Best (12 mo)",
      "bondCoupon": "Bond Coupon",
      "expenses": "Expenses",
      "fiveYrGrowth": "5-yr Growth",
      "netWorth": "Net Worth",
      "netWorth2026": "Net Worth 2026",
      "netWorth2030": "Net Worth 2030",
      "occupied": "occupied",
      "owner": "Owner",
      "property": "Property",
      "propertyVal": "Property Val",
      "propertyValues": "Property Values — {{year}}",
      "rentalNet": "Rental Net",
      "scenario": "Scenario",
      "surplus": "Surplus",
      "totalSurplus": "Total surplus",
      "value2026": "Value 2026",
      "worst": "Worst (8 mo)",
      "year": "Year",
      "yearByYear": "Year-by-Year Summary"
    },
    "imminentAlert": "IMMINENT — {{symbol}} due {{maturity}}",
    "imminentAlertDesc": "Principal: {{principal}}. Review liquidity and reinvestment options.",
    "monthly": {
      "beforeTax": "Before tax · 10% withholding applies at source",
      "belowFloor": "✗ Below floor",
      "bestTerm": "Best Term",
      "currentLiquidCash": "Current Liquid Cash",
      "deployableSurplus": "Deployable Surplus",
      "depositLadder": "Fidelis R3202A Feb 2027 — Deposit Ladder",
      "estNet6m": "Est. Net / 6M",
      "liquidCash": "Liquid Cash",
      "liquidCashDesc": "Enter total available liquid cash (savings and current accounts) in RON.",
      "liquidityChecklist": "Liquidity Checklist",
      "liquidityFloor": "Liquidity Floor (3×exp)",
      "liquidityOk": "✓ Liquidity OK",
      "maturity": "Maturity",
      "netInterest": "Net Interest",
      "principal": "Principal",
      "rate": "Rate",
      "rateYear": "Rate ({{year}})",
      "suggestedPrincipal": "Suggested Principal",
      "tbiRatesForecast": "TBI Rates Forecast (2026–2030)",
      "tbiRecommendation": "TBI Deposit Recommendation",
      "tranche": "Tranche"
    },
    "occupancy": {
      "desc": "Average months rented per year (drag to recalculate)",
      "title": "Occupancy Assumption"
    },
    "scenario": {
      "annual": "Annual",
      "baseline": "(baseline)",
      "bondsEquiv": "Bonds equiv",
      "expenses": "Expenses",
      "months": "Months",
      "pension": "Pension",
      "rentalNet": "Rental net",
      "subtitle": "All figures in RON/month · Bonds treated as monthly equivalent",
      "surplus": "Surplus",
      "title": "Occupancy Sensitivity (8–12 months)",
      "totalIncome": "Total income"
    },
    "stats": {
      "annualSurplus": "Annual Surplus",
      "bondsEquiv": "Bonds equiv / mo",
      "bufferNeeded": "Buffer Needed",
      "expenses": "Expenses / mo",
      "netSurplus": "Net Surplus / mo",
      "noneOk": "None ✓",
      "pension": "Pension / mo",
      "rentalNet": "Rental Net / mo",
      "totalIncome": "Total Income / mo"
    },
    "tabs": {
      "fiveYear": "5-Year Plan",
      "monthly": "Monthly Manager",
      "overview": "Overview"
    },
    "tbi": {
      "imminent": "IMMINENT",
      "maturity": "Maturity",
      "netPerYear": "Net/yr",
      "rate": "Rate",
      "soon": "Soon"
    },
    "totals": {
      "grossYr": "Gross / yr",
      "monthlyEquiv": "Monthly equiv",
      "netYr": "Net / yr",
      "title": "Bond Portfolio Totals"
    }
  },
  "payments": {
    "title": "Payments",
    "fieldTitle": "Title",
    "subtitle": "Manage rent and utility payments",
    "addPayment": "Add Payment",
    "recordPayment": "Record Payment",
    "filterByStatus": "Filter by status",
    "filterByType": "Filter by type",
    "allStatuses": "All statuses",
    "allTypes": "All types",
    "noPayments": "No payments found",
    "noPaymentsFound": "No payments found with these filters",
    "searchPlaceholder": "Search payments...",
    "pending": "Pending",
    "partial": "Partial",
    "paid": "Paid",
    "overdue": "Overdue",
    "cancelled": "Cancelled",
    "rent": "Rent",
    "utilities": "Utilities",
    "deposit": "Deposit",
    "maintenance": "Maintenance",
    "other": "Other",
    "paidAmount": "Paid:",
    "viewDetails": "View payment details",
    "markPaid": "Mark Paid",
    "markedPaidSuccess": "Payment marked as paid",
    "updateError": "Error updating payment",
    "sendReminder": "Send Reminder",
    "reminderSent": "Reminder sent",
    "reminderError": "Failed to send reminder",
    "totalPayments": "Total payments",
    "collectedThisMonth": "Collected this month",
    "payment": "Payment",
    "tenant": "Tenant",
    "apartment": "Apartment",
    "type": "Type",
    "amount": "Amount",
    "dueDate": "Due date",
    "paidDate": "Paid Date",
    "status": "Status",
    "actions": "Actions"
  },
  "tasks": {
    "title": "Tasks",
    "subtitle": "Manage family tasks",
    "addTask": "Add Task",
    "newTask": "New Task",
    "noTasks": "No tasks",
    "taskOptions": "Task options",
    "filterByProject": "Filter by project",
    "filterByBuilding": "Filter by building",
    "filterByApartment": "Filter by apartment",
    "allProjects": "All projects",
    "allBuildings": "All buildings",
    "allApartments": "All apartments",
    "allPriorities": "All priorities",
    "allTypes": "All types",
    "searchPlaceholder": "Search tasks…",
    "clearFilters": "Clear filters",
    "labelFilterPlaceholder": "Label",
    "showCancelled": "Show cancelled",
    "boardView": "Board",
    "listView": "List",
    "viewToggle": "View mode",
    "boardRegion": "Task board",
    "listRegion": "Task list",
    "listCount": "{{count}} tasks",
    "loadMore": "Load more",
    "openTask": "Open task: {{title}}",
    "dragHandle": "Drag to reorder",
    "quickAddLabel": "Quick add task title",
    "quickAddPlaceholder": "Task title, press Enter…",
    "updateError": "Error updating task",
    "todo": "To do",
    "inProgress": "In progress",
    "waiting": "Waiting",
    "done": "Done",
    "cancelled": "Cancelled",
    "low": "Low",
    "medium": "Medium",
    "high": "High",
    "typePayment": "Payment",
    "typeMedical": "Medical",
    "typeRepair": "Repair",
    "typeDocument": "Document",
    "typeContact": "Contact",
    "typeMeeting": "Meeting",
    "typeOther": "Other",
    "typeGeneral": "General",
    "typeInspection": "Inspection",
    "typePurchase": "Purchase",
    "newEntry": "New Task / Maintenance Entry",
    "titleLabel": "Title",
    "titleRequired": "Title *",
    "titlePlaceholder": "Task title...",
    "descriptionPlaceholder": "Optional description...",
    "descriptionLabel": "Description",
    "description": "Description",
    "statusLabel": "Status",
    "status": "Status",
    "typeLabel": "Type",
    "type": "Type",
    "priorityLabel": "Priority",
    "priority": "Priority",
    "dueDateLabel": "Due date",
    "dueDate": "Due date",
    "budgetLabel": "Budget (€)",
    "budget": "Budget",
    "project": "Project",
    "noProject": "No project",
    "building": "Building",
    "noBuilding": "Not linked to a building",
    "apartment": "Apartment",
    "noApartment": "No apartment",
    "selectBuildingFirst": "Select a building first",
    "labels": "Labels",
    "labelsPlaceholder": "Add label…",
    "checklist": "Checklist",
    "noChecklist": "No checklist items",
    "addChecklistPlaceholder": "Add checklist item…",
    "comments": "Comments",
    "noComments": "No comments yet",
    "addCommentPlaceholder": "Write a comment…",
    "postComment": "Post",
    "linkedIdentities": "Linked people",
    "dependencies": "Dependencies",
    "blockedBy": "Blocked by {{title}}",
    "sourceKey": "Source key",
    "loadingTask": "Loading task…",
    "loadTaskError": "Could not load task",
    "taskRef": "Task #{{id}}",
    "unknownAuthor": "Unknown",
    "commentError": "Could not add comment",
    "checklistError": "Could not update checklist",
    "assignee": "Assignee",
    "createButton": "Create",
    "addEntryButton": "Add Entry",
    "dueLabel": "Due {{date}}",
    "overdue": "Overdue",
    "taskCreated": "Task created",
    "taskCreatedSuccess": "Task created",
    "taskCreateError": "Error creating task",
    "createError": "Error creating task",
    "editTask": "Edit task",
    "taskUpdated": "Task updated",
    "taskDeleted": "Task deleted",
    "deleteError": "Error deleting task",
    "confirmDelete": "Delete task?",
    "confirmDeleteMessage": "Are you sure you want to delete \"{{title}}\"? This action cannot be undone."
  },
  "properties": {
    "title": "Properties",
    "subtitle": "Manage buildings, apartments and tenants",
    "addBuilding": "Add Building",
    "addApartment": "Add Apartment",
    "addTenant": "Add Tenant",
    "buildings": "Buildings",
    "apartments": "Apartments",
    "tenants": "Tenants",
    "editApartment": "Edit apartment",
    "editTenant": "Edit tenant",
    "deleteTenant": "Delete tenant",
    "tenantDeleted": "Tenant deleted",
    "tenantDeleteError": "Error deleting tenant",
    "confirmDeleteTenant": "Are you sure you want to delete this tenant?",
    "noBuildings": "No buildings",
    "addFirstBuilding": "Add your first building to get started",
    "allBuildings": "All buildings",
    "noApartments": "No apartments",
    "noTenants": "No tenants",
    "surface": "Surface",
    "rooms": "Rooms",
    "rent": "Rent",
    "occupied": "Occupied",
    "vacant": "Vacant",
    "bills": "Bills",
    "addBill": "Add Bill",
    "noBills": "No bills recorded",
    "groupedByBuilding": "Apartments grouped by building",
    "suggestedDocuments": "Suggested Documents",
    "viewAllDocs": "View all in Documents",
    "archiveApartment": "Archive apartment",
    "unarchiveApartment": "Restore apartment",
    "archived": "Archived",
    "showArchived": "Show archived",
    "hideArchived": "Hide archived",
    "archiveSuccess": "Apartment archived",
    "unarchiveSuccess": "Apartment restored",
    "building": "Building",
    "selectBuilding": "Select building",
    "noBuildingsYet": "No buildings yet",
    "apartmentName": "Apartment name",
    "apartmentNamePlaceholder": "e.g. Ap. 3",
    "floor": "Floor",
    "bathrooms": "Bathrooms",
    "status": "Status",
    "maintenance": "Maintenance",
    "reserved": "Reserved",
    "contractType": "Contract type",
    "contractRental": "Rental",
    "contractComodat": "Comodat",
    "contractAirbnb": "Airbnb",
    "contractOther": "Other",
    "monthlyRent": "Monthly rent",
    "deposit": "Deposit",
    "rentDueDay": "Rent due day",
    "hasParking": "Parking",
    "hasStorage": "Storage",
    "isFurnished": "Furnished",
    "notes": "Notes",
    "notesPlaceholder": "Additional notes...",
    "buildingRequired": "Please select a building",
    "createBuilding": "Create building",
    "buildingCreated": "Building added",
    "buildingSaveError": "Error saving building",
    "buildingNamePlaceholder": "e.g. Tineretului 31A",
    "address": "Address",
    "city": "City",
    "nameRequired": "Name is required",
    "apartmentCreated": "Apartment added successfully",
    "apartmentUpdated": "Apartment updated",
    "apartmentSaveError": "Error saving apartment",
    "firstName": "First name",
    "lastName": "Last name",
    "phone": "Phone",
    "email": "Email",
    "apartment": "Apartment",
    "noApartmentAssigned": "No apartment assigned",
    "moveInDate": "Move-in date",
    "contractEnd": "Contract end",
    "cnp": "ID / CNP",
    "cnpPlaceholder": "13-digit CNP",
    "permanentAddress": "Permanent address",
    "tenantCreated": "Tenant added successfully",
    "tenantUpdated": "Tenant updated",
    "tenantSaveError": "Error saving tenant",
    "archiveError": "Error archiving apartment",
    "addFirstApartment": "Add your first apartment to get started",
    "viewDoc": "View",
    "addFromDoc": "Add apartment",
    "addTenantFromDoc": "Add tenant",
    "fillFromDocument": "Fill from document",
    "searchDocuments": "Search documents...",
    "noDocsFound": "No documents found",
    "noExtractedData": "No data extracted",
    "useProfile": "Use",
    "tabEstate": "Estate Management",
    "tabAdmin": "Administration",
    "buildingInfo": "Building Information",
    "county": "County",
    "floors": "Floors",
    "yearBuilt": "Year Built",
    "parkingSpots": "Parking Spots",
    "administrator": "Administrator",
    "adminPhone": "Admin Phone",
    "adminEmail": "Admin Email",
    "noApartmentsYet": "No apartments yet",
    "floorLabel": "Floor {{floor}}",
    "roomsCount": "{{count}} rooms",
    "rentDueBadge": "Rent due",
    "leavingLabel": "Leaving",
    "todayLabel": "today",
    "tomorrowLabel": "tomorrow",
    "daysLeftShort": "{{count}}d",
    "rentPerMonth": "RON/mo",
    "perMonth": "/mo",
    "statOpenMaintenance": "Open Maintenance",
    "statMaintenanceBudget": "Maintenance Budget",
    "statOpenTasks": "Open Tasks",
    "statApartments": "Apartments",
    "statOccupied": "{{count}} occupied",
    "statTotal": "{{count}} total",
    "statEstimated": "estimated",
    "buildingNotFound": "Building not found",
    "backToProperties": "Back to Properties",
    "noMaintenanceEntries": "No maintenance entries for this building",
    "noTasksForBuilding": "No tasks for this building",
    "confirmDeleteFull": "Delete \"{{name}}\"? This cannot be undone. All contracts and links will also be removed.",
    "confirmDeleteSimple": "Delete \"{{name}}\"? This cannot be undone.",
    "aptNameNumber": "Apartment Name / Number",
    "aptNamePlaceholder": "e.g. Apt 3A",
    "surfaceM2": "Surface (m²)",
    "currency": "Currency",
    "editApartmentTitle": "Edit Apartment",
    "saveChanges": "Save Changes",
    "deletingLabel": "Deleting…",
    "savingLabel": "Saving…",
    "apartmentDeletedError": "Failed to delete apartment",
    "archiveUpdateError": "Failed to update apartment",
    "tabOverview": "Overview",
    "tabMaintenanceLabel": "Maintenance",
    "tabTasksLabel": "Tasks",
    "allPaid": "All paid ✓",
    "noTextExtracted": "No text extracted yet",
    "identityNotFound": "Not found",
    "identityRoles": "Roles"
  },
  "estate": {
    "assignPerson": "Assign Person",
    "role": "Role",
    "roles": {
      "tenant": "Tenant",
      "resident": "Resident",
      "comodatar": "Borrower",
      "owner": "Owner",
      "comodant": "Lender",
      "guarantor": "Guarantor",
      "agent": "Agent",
      "other": "Other"
    },
    "roleGroupPermanent": "Permanent",
    "roleGroupContractual": "Contractual / Renters",
    "startDate": "Start Date",
    "endDate": "End Date (optional)",
    "assignRole": "Assign Role",
    "roleAssigned": "Role assigned successfully",
    "roleAssignError": "Failed to assign role",
    "selectIdentityRequired": "Please select a person or company",
    "since": "since",
    "person": "Person / Company",
    "noIdentitiesFound": "No entries found",
    "removeRole": "Remove Role",
    "removeRoleError": "Failed to remove role",
    "noRoles": "No people assigned",
    "assignFirst": "Assign the first person to this apartment"
  },
  "administration": {
    "people": "People",
    "companies": "Companies",
    "bills": "Bills",
    "noPeople": "No people added yet",
    "noCompanies": "No companies added yet",
    "allBills": "All"
  },
  "documents": {
    "title": "Documents",
    "subtitle": "Manage your documents",
    "uploadDocuments": "Upload Documents",
    "uploadDocument": "Upload Document",
    "dropFilesHere": "Drop files here...",
    "supportedFormats": "PDF, images or Word documents",
    "noDocuments": "No documents",
    "noDocumentsFiltered": "No documents found with these filters",
    "startByUploading": "Start by uploading your first document",
    "searchDocuments": "Search documents...",
    "filterByCategory": "Filter by category",
    "filterByStatus": "Filter by status",
    "allCategories": "All categories",
    "allStatuses": "All statuses",
    "pending": "Pending",
    "processing": "Processing",
    "processed": "Processed",
    "failed": "Error",
    "showingResults": "Showing {{count}} of {{total}} documents",
    "paginatedResults": "Results loaded from the paginated backend list",
    "arrayResults": "Results loaded from the flat backend list",
    "documentOptions": "Document options",
    "view": "View",
    "preview": "Preview",
    "previewUnavailable": "Preview is not available for this file type yet.",
    "previewLoadError": "The preview could not be loaded inline. You can still download the file.",
    "previewReturnedHtml": "The preview URL returned the app page instead of the document file. Check the media path or proxy configuration.",
    "fileUnavailable": "This document does not currently have a downloadable file.",
    "download": "Download",
    "delete": "Delete",
    "documentDeleted": "Document deleted",
    "documentDeleteError": "Error deleting document",
    "dragFilesOrClick": "Drag files here or click to select",
    "close": "Close",
    "confirmDelete": "Are you sure you want to delete this document?",
    "searchable": "Searchable",
    "notSearchable": "Not searchable",
    "notSearchableHelp": "This file has no OCR text yet. You can still add labels to make it easier to organize.",
    "processingIssuesTitle": "Processing issues",
    "ocrFailureTitle": "OCR failed",
    "aiLabelingFailureTitle": "AI labeling failed",
    "uploadedAtLabel": "Uploaded {{date}}",
    "searchText": "Extracted text",
    "labelsTitle": "Labels / tags",
    "labelsHelp": "Add comma-separated labels to help admins and users organize non-searchable files.",
    "noLabels": "No labels yet",
    "labelsInput": "Labels",
    "labelsPlaceholder": "invoice, contract, important",
    "saveLabels": "Save labels",
    "labelsSaved": "Labels saved",
    "labelsError": "Could not save labels",
    "categoryTitle": "Category",
    "noCategory": "No category",
    "saveCategory": "Save category",
    "categorySaved": "Category saved",
    "categoryError": "Could not save category",
    "suggestedTagsTitle": "AI suggestions",
    "suggestedTagsHelp": "Review suggested tags before promoting them to approved labels. Suggested tags remain searchable in both Romanian and English.",
    "aiTagBadge": "AI",
    "aiConfidence": "Confidence {{value}}%",
    "approveTag": "Approve",
    "rejectTag": "Reject",
    "tagApproved": "Suggested tag approved",
    "tagRejected": "Suggested tag rejected",
    "tagApproveError": "Could not approve suggested tag",
    "tagRejectError": "Could not reject suggested tag",
    "signatoriesTitle": "Signatories",
    "signatoriesHelp": "People who signed this document, extracted during AI analysis.",
    "signatoryRole": "Role",
    "signatoryEntity": "Entity",
    "signatoryType": "Signature type",
    "gridView": "Grid view",
    "listView": "List view",
    "selectMode": "Select",
    "cancelSelection": "Cancel selection",
    "selectedCount": "{{count}} selected",
    "selectAll": "Select all",
    "selectAllOnPage": "Select all on this page ({{count}})",
    "deselectAll": "Deselect all",
    "bulkAddLabels": "Add labels",
    "bulkSetCategory": "Set category",
    "bulkCategoryApplied": "Category applied to selected documents",
    "bulkDelete": "Delete selected",
    "bulkDeleteConfirm": "Delete {{count}} document(s)? This cannot be undone.",
    "bulkLabelsApplied": "Labels applied to selected documents",
    "bulkDeleteDone": "Selected documents deleted",
    "page": "Page",
    "pageSize": "Per page",
    "previousPage": "Previous page",
    "nextPage": "Next page",
    "queueTitle": "Processing queue",
    "queueDescription": "Monitor files waiting to be scanned and processed by OCR.",
    "queueActive": "active",
    "queueFailed": "failed",
    "queueEmpty": "Queue is empty — no files awaiting processing",
    "queueEmptyHint": "New documents dropped in the scanner inbox will appear here.",
    "queueInbox": "In scanner inbox",
    "queueWaiting": "Waiting",
    "queueProcessing": "Being processed",
    "queueRecentlyFailed": "Recently failed (24 h)",
    "queueLastUpdated": "Updated",
    "updated": "Updated",
    "reprocess": "Reprocess",
    "reprocessStarted": "Re-processing started",
    "reprocessError": "Failed to start reprocessing",
    "relabel": "Re-label",
    "relabelStarted": "Re-labeling queued",
    "relabelError": "Failed to queue re-labeling",
    "retitle": "Retitle",
    "retitleStarted": "Title regeneration queued",
    "retitleError": "Failed to queue title regeneration",
    "confirmApprove": "Confirm",
    "cancel": "Cancel",
    "bulkRelabel": "Re-label selected",
    "bulkRelabelDone": "{{count}} document(s) queued for re-labeling",
    "bulkRelabelError": "Failed to queue re-labeling",
    "bulkRetitle": "Retitle selected",
    "bulkRetitleDone": "{{count}} document(s) queued for title regeneration",
    "bulkRetitleError": "Failed to queue title regeneration",
    "aiLabelingQueue": "AI Labeling Queue",
    "aiLabelingIdle": "No AI labeling jobs in progress",
    "aiLabelingInProgress": "Labeling in progress",
    "aiLabelingRecentlyFailed": "Labeling failed (24 h)",
    "aiLabelingStatusInProcessing": "Labeling…",
    "aiLabelingStatusCompleted": "Labeled",
    "aiLabelingStatusFailed": "Label failed",
    "aiLabelingStatusNotProcessed": "Not labeled",
    "filterByAiLabeling": "Filter by AI labeling",
    "allAiStatuses": "All AI statuses",
    "includeAiTags": "AI tags",
    "semanticSearch": "Semantic",
    "semanticSearchHelp": "Search by meaning using document embeddings",
    "includeAiTagsHelp": "When off, AI-suggested (pending) tags are excluded from search matching",
    "aiFeatures": "AI labeling & retitling",
    "aiNotLabeled": "unlabeled",
    "tableView": "Table view",
    "colName": "Name",
    "colRank": "Rank",
    "colStatus": "Status",
    "colCategory": "Category",
    "colType": "Type",
    "colSize": "Size",
    "colDate": "Uploaded",
    "filterPlaceholder": "Filter…",
    "clearFilters": "Clear filters",
    "tableViewError": "Table view error — try reloading the page.",
    "viewProfiles": "View Profiles",
    "noProfilesYet": "No saved profiles yet",
    "profilesLabel": "Profiles",
    "resizePanelHint": "Drag to resize panels",
    "copyPathInline": "Copy path",
    "openInExplorer": "Open in Explorer",
    "openInExplorerOk": "Opening Explorer…",
    "openInExplorerFallback": "Could not open Explorer automatically. Copy the path manually.",
    "scannerFolder": "Scanner folder",
    "openScannerFolder": "Open scanner folder",
    "scannerFolderTitle": "Scanner inbox folder",
    "scannerFolderDesc": "Drop any PDF or image file into this folder. The server will detect it automatically and process it into your document library.",
    "containerPath": "Container path",
    "hostPath": "Host (Windows) path",
    "clickToEdit": "Click to edit",
    "restartRequired": "Path saved. Restart the stack (docker compose up -d) for the scanner_watcher container to monitor the new folder.",
    "folderExists": "Folder exists",
    "folderMissing": "Folder will be created on next activity",
    "acceptedTypes": "Accepted",
    "scanNowTitle": "Queue all files in the inbox for immediate processing",
    "scanning": "Scanning…",
    "scanNow": "Scan now",
    "pathCopied": "Path copied to clipboard",
    "copyPath": "Copy path",
    "scannerSaveError": "Failed to save scanner path",
    "scanNowEmpty": "Inbox is empty — nothing to process.",
    "scanNowQueued": "{{count}} file(s) queued for processing.",
    "scanNowError": "Scan failed. Check server logs.",
    "archive": "Archive",
    "unarchive": "Restore",
    "archiveSuccess": "Document archived",
    "unarchiveSuccess": "Document restored",
    "showArchived": "Show archived",
    "hideArchived": "Hide archived"
  },
  "family": {
    "title": "Family",
    "subtitle": "Manage your family members and relations",
    "members": "Members",
    "addMember": "Add Member",
    "noMembers": "No family members yet",
    "role": "Role",
    "owner": "Owner",
    "member": "Member",
    "relation": "Relation",
    "relations": "Relations",
    "tree": "Family Tree",
    "name": "Name",
    "phone": "Phone",
    "email": "Email",
    "editMember": "Edit member",
    "deleteMember": "Delete member",
    "memberDeleted": "Member removed",
    "memberDeleteError": "Error removing member",
    "confirmDeleteMember": "Remove this family member?",
    "fullName": "Full name",
    "fullNamePlaceholder": "e.g. Popescu Ion",
    "displayName": "Display name",
    "displayNamePlaceholder": "e.g. dad, mom, me",
    "displayNameHint": "Unique short code used in document tags (lowercase, no spaces)",
    "nameAliases": "Name aliases",
    "nameAliasesPlaceholder": "POPESCU ION, Ion Popescu",
    "nameAliasesHint": "Auto-generated from full name. Edit to add custom aliases for OCR matching.",
    "tenantSuggestions": "Suggest from tenants",
    "isOwner": "Primary owner",
    "isActive": "Active",
    "inactive": "Inactive",
    "alsoKnownAs": "Also known as",
    "memberCreated": "Member added",
    "memberUpdated": "Member updated",
    "memberSaveError": "Error saving member",
    "fullNameRequired": "Full name is required",
    "displayNameRequired": "Display name is required",
    "noRelations": "No relations defined",
    "addRelation": "Add Relation",
    "fromMember": "From",
    "toMember": "To",
    "relationType": "Type",
    "relationLabel": "Label (optional)",
    "selectMember": "Select member",
    "selectType": "Select type",
    "relationCreated": "Relation added",
    "relationCreateError": "Error adding relation",
    "relationDeleted": "Relation removed",
    "relationDeleteError": "Error removing relation",
    "confirmDeleteRelation": "Remove this relation?",
    "spouse": "Spouse",
    "parent": "Parent",
    "child": "Child",
    "sibling": "Sibling",
    "grandparent": "Grandparent",
    "grandchild": "Grandchild",
    "other": "Other"
  },
  "notFound": {
    "title": "Page Not Found",
    "description": "The page you're looking for doesn't exist or has been moved.",
    "backHome": "Back Home"
  },
  "identities": {
    "tabLabel": "Identities",
    "addTitle": "Add Person / Company",
    "editTitle": "Edit Person / Company",
    "name": "Name",
    "firstName": "Given Name (Prenume)",
    "lastName": "Family Name (Nume)",
    "type": "Type",
    "typePerson": "Person (Persoană fizică)",
    "typeCompany": "Company / Firm",
    "cnp": "CNP (Personal Numeric Code)",
    "cui": "CUI (Company Registration No.)",
    "phone": "Phone",
    "email": "Email",
    "address": "Address",
    "notes": "Notes",
    "empty": "No identities yet",
    "created": "Person / company added",
    "updated": "Updated successfully",
    "deleted": "Deleted",
    "saveError": "Failed to save",
    "deleteError": "Failed to delete",
    "confirmDelete": "Are you sure you want to delete this entry?"
  },
  "links": {
    "recommendations": "AI Recommendations",
    "noRecs": "No recommendations yet",
    "noRecsHint": "Click \"Find connections\" to let AI suggest links",
    "loadRecs": "Find connections",
    "loading": "Searching for connections…",
    "accept": "Accept",
    "reject": "Dismiss",
    "accepted": "Link accepted",
    "rejected": "Dismissed",
    "addManual": "Add link manually",
    "label": "Relationship",
    "confidence": "Confidence",
    "status": {
      "pending": "Pending review",
      "accepted": "Confirmed",
      "rejected": "Dismissed",
      "manual": "Manual"
    },
    "labels": {
      "mentioned_in": "Mentioned in",
      "mentions": "Mentions",
      "references_building": "References building",
      "references_apartment": "References apartment",
      "referenced_in": "Referenced in",
      "owner_of": "Owner of",
      "tenant_at": "Tenant at",
      "guarantor_for": "Guarantor for"
    },
    "entityTypes": {
      "identity": "Person / Company",
      "apartment": "Apartment",
      "building": "Building",
      "document": "Document",
      "task": "Task",
      "payment": "Payment"
    },
    "noBuildingsYet": "No buildings — create one first",
    "noBuildings": "No buildings yet"
  },
  "llm": {
    "offline": "AI assistant offline",
    "requiresLlm": "requires the LLM to be running",
    "start": "Start LLM",
    "stop": "Stop LLM",
    "starting": "Starting…",
    "stopping": "Stopping…",
    "startSuccess": "LLM is starting up",
    "stopSuccess": "LLM stopped",
    "startError": "Failed to start LLM",
    "stopError": "Failed to stop LLM",
    "adminOnly": "Only admins can start the LLM (explicitly stopped by admin)",
    "status": {
      "running": "Running",
      "stopped_auto": "Idle / auto-sleep",
      "stopped_admin": "Stopped by admin"
    }
  },
  "hoaCharges": {
    "title": "HOA Charges",
    "subtitle": "Building maintenance charges overview",
    "totalCharges": "Total Charges",
    "unpaidTotal": "Unpaid Total",
    "ownerUnpaid": "Owner Unpaid",
    "renterUnpaid": "Renter Unpaid",
    "charges": "charges",
    "filters": "Filters",
    "allBuildings": "All Buildings",
    "allStatuses": "All Statuses",
    "allResponsible": "All Responsible",
    "unpaid": "Unpaid",
    "paid": "Paid",
    "owner": "Owner",
    "renter": "Renter",
    "buildingApartment": "Building / Apartment",
    "category": "Category",
    "period": "Period",
    "amount": "Amount",
    "responsible": "Responsible",
    "status": "Status",
    "actions": "Actions",
    "markPaid": "Mark Paid",
    "noCharges": "No HOA charges found.",
    "markPaidSuccess": "Charge marked as paid",
    "markPaidError": "Failed to mark charge as paid"
  },
  "apartmentToasts": {
    "deleted": "Apartment deleted",
    "deleteFailed": "Failed to delete apartment",
    "taskCreated": "Task created",
    "taskCreateError": "Error creating task",
    "taskUpdateError": "Error updating task",
    "roleRemoved": "Role removed",
    "roleRemoveFailed": "Failed to remove role",
    "legacyTenantRemoved": "Legacy tenant removed",
    "tenantRemoveFailed": "Failed to remove tenant",
    "tenantUpdated": "Tenant updated",
    "tenantUpdateFailed": "Failed to update tenant",
    "tenantDeleteFailed": "Failed to delete tenant",
    "converted": "Converted to standard entity and assigned as tenant",
    "conversionFailed": "Conversion failed"
  },
  "financesToasts": {
    "holdingAdded": "Holding added",
    "holdingUpdated": "Holding updated",
    "saveFailed": "Save failed",
    "holdingDeleted": "Holding deleted",
    "deleteFailed": "Delete failed",
    "holdingMoved": "Holding moved",
    "tradevilleImportFailed": "Tradeville import failed",
    "revolutImportFailed": "Revolut import failed",
    "etoroImportFailed": "eToro statement import failed",
    "binanceImportFailed": "Binance import failed",
    "importWithErrors": "Imported with errors: {{errors}}",
    "importSuccess": "{{created}} created, {{updated}} updated",
    "etoroSyncFailed": "eToro API sync failed",
    "priceUpdateWithErrors": "Updated {{updated}}, errors: {{errors}}",
    "pricesUpdated": "Prices updated: {{updated}} holdings",
    "priceRefreshFailed": "Price refresh failed",
    "depositRecomputed": "Deposit values recomputed: {{updated}}",
    "depositRecomputeFailed": "Deposit recompute failed",
    "gfSyncWithErrors": "Synced {{imported}}, errors: {{errors}}",
    "gfSyncSuccess": "Ghostfolio: {{imported}} activities imported",
    "gfSyncFailed": "Ghostfolio sync failed",
    "moveFailed": "Move failed"
  },
  "utilityToasts": {
    "markedPaid": "Marked as paid",
    "markPaidFailed": "Failed to mark as paid",
    "syncQueued": "Sync queued",
    "syncFailed": "Sync failed",
    "historicalPullQueued": "Historical pull queued (2 years of email + portal data)",
    "historyPullFailed": "Failed to queue history pull",
    "readingSaved": "Reading saved",
    "readingSaveFailed": "Failed to save reading",
    "readingDeleteFailed": "Failed to delete reading"
  },
  "bundlesToasts": {
    "created": "Bundle \"{{name}}\" created",
    "createFailed": "Failed to create bundle",
    "createdWithDocs": "Bundle \"{{name}}\" created with documents",
    "createFromSuggestionFailed": "Failed to create bundle from suggestion",
    "deleted": "Bundle deleted",
    "deleteFailed": "Failed to delete bundle",
    "smartRefreshed": "Smart bundle refreshed: +{{added}} / -{{removed}} documents",
    "refreshFailed": "Failed to refresh smart bundle",
    "docsRemoved": "{{count}} document(s) removed from bundle",
    "removeFailed": "Failed to remove documents"
  },
  "documentsToasts": {
    "defaultProfileSet": "Default profile set",
    "recommendFailed": "Failed to generate recommendations",
    "profileSaved": "Profile \"{{name}}\" saved"
  },
  "career": {
    "profiles": {
      "label": "Candidate profile",
      "select": "Select a profile",
      "selectionRequired": "Choose a candidate profile to continue",
      "workingOn": "Working on {{name}}",
      "loadError": "Could not load candidate profiles",
      "readOnly": "Read-only access: you can view this profile but not change it. Scans, answers and other changes will be refused."
    },
    "djinni": {
      "notChecked": "Not checked",
      "checking": "Checking…",
      "check": "Check Djinni",
      "verified": "Last verified",
      "replied": "Employer has replied",
      "awaiting": "No employer reply found",
      "unclear": "Reply status unclear",
      "open": "Open conversation",
      "reauth": "Djinni sign-in needs renewing. Saved status is retained.",
      "failed": "Could not verify the latest status. Saved status is retained."
    },
    "title": "Career Dashboard",
    "subtitle": "Job search pipeline, offers & evaluations",
    "bridgeOnline": "Bridge online",
    "bridgeOffline": "Bridge offline",
    "bridgeChecking": "Checking bridge…",
    "bridgeNoAccess": "No career profile access",
    "tabs": {
      "tracker": "Tracker",
      "pipeline": "Pipeline",
      "reports": "Reports",
      "scanner": "Scanner",
      "liveness": "Liveness",
      "questions": "Questions"
    },
    "tracker": {
      "failedToLoad": "Failed to load tracker",
      "applicationCount": "{{count}} applications",
      "applicationCountFiltered": "{{filtered}} of {{total}} applications",
      "refresh": "Refresh",
      "searchPlaceholder": "Search company, role...",
      "allPlatforms": "All Platforms",
      "allStatuses": "All Statuses",
      "minScore": "Min Score:",
      "clearFilters": "Clear",
      "colNumber": "#",
      "colDate": "Date",
      "colCompany": "Company",
      "colRole": "Role",
      "colPlatform": "Platform",
      "colScore": "Score",
      "colStatus": "Status",
      "colPdf": "PDF",
      "colReport": "Report",
      "colNotes": "Notes",
      "noMatchingApps": "No applications match your filters",
      "noAppsYet": "No applications yet",
      "allPdfStatus": "All PDF",
      "hasPdf": "Has PDF ✅",
      "noPdf": "No PDF ❌",
      "openPdf": "Download generated CV",
      "pdfFailed": "Could not download CV. Please try again."
    },
    "pipeline": {
      "failedToLoad": "Failed to load pipeline",
      "title": "Pipeline Inbox",
      "noPendingUrls": "No pending URLs"
    },
    "reports": {
      "reportCount": "{{count}} Reports",
      "emptyReport": "Empty report",
      "selectReport": "Select a report to view"
    },
    "scanner": {
      "title": "Portal Scanner",
      "quickScan": "Quick Scan",
      "fullScan": "Full Scan",
      "scanning": "Scanning portals... this may take a few minutes"
    },
    "batch": {
      "title": "Batch Evaluation",
      "running": "(Running)",
      "complete": "(Complete)",
      "paused": "(Paused)",
      "doneCount": "{{completed}}/{{total}} done · {{failed}} failed · avg {{avg}}",
      "topScores": "Top scores:"
    },
    "liveness": {
      "title": "Position Availability Checker",
      "urlsLabel": "Job posting URLs (one per line)",
      "checkBtn": "Check Availability",
      "loadTracker": "Load from Tracker",
      "useOllama": "Use Ollama for uncertain",
      "checking": "Checking URLs... This may take a while for large batches.",
      "checkFailed": "Liveness check failed",
      "noUrls": "Enter at least one URL",
      "colStatus": "Status",
      "colUrl": "URL"
    },
    "questions": {
      "title": "Skill Questions",
      "subtitle": "Answer questions to improve CV accuracy",
      "queue": "Priority Queue",
      "allQuestions": "All Questions",
      "stats": "{{answered}} of {{total}} answered",
      "noQuestions": "No questions yet. Questions will appear here when CV generation encounters uncertain skills.",
      "noUnanswered": "All questions answered! Your profile is fully calibrated.",
      "category": "Category",
      "allCategories": "All Categories",
      "answered": "Answered",
      "unanswered": "Unanswered",
      "all": "All",
      "answerPlaceholder": "Type your answer...",
      "confidenceLabel": "Confidence (1-5)",
      "submit": "Submit Answer",
      "submitting": "Submitting...",
      "positions": "{{count}} positions",
      "answerSuccess": "Answer saved successfully",
      "answerError": "Failed to save answer",
      "loadError": "Failed to load questions",
      "loading": "Loading questions...",
      "noPositionQuestions": "No questions for {{company}} — {{role}}",
      "positionQuestions": "{{count}} questions for {{company}} — {{role}}",
      "yourAnswer": "Your answer",
      "editAnswer": "Edit",
      "calibrated": "Calibrated",
      "pending": "Pending calibration"
    },
    "retry": "Retry"
  },
  "careerToasts": {
    "scanCompleted": "Scan completed",
    "scanFailed": "Scan failed"
  },
  "settingsToasts": {
    "disconnectFailed": "Could not disconnect"
  },
  "history": {
    "filterAll": "All",
    "filterOccupation": "Occupation",
    "filterMoney": "Money",
    "filterMaintenance": "Maintenance",
    "filterTasks": "Tasks",
    "dateRange6mo": "6 mo",
    "dateRange1yr": "1 yr",
    "dateRange2yr": "2 yr",
    "dateRangeAll": "All",
    "apartmentLabel": "Apartment:",
    "noEvents": "No events match the current filters",
    "noEventsHint": "Try adjusting the category or date range filters above.",
    "present": "present",
    "budgetPrefix": "Budget:",
    "paymentRent": "Rent",
    "paymentDeposit": "Deposit",
    "paymentUtilities": "Utilities",
    "paymentMaintenance": "Maintenance",
    "paymentRepair": "Repair",
    "paymentOther": "Payment"
  },
  "utility": {
    "noInvoice": "No invoice",
    "paid": "Paid",
    "overdue": "Overdue",
    "dueInDays": "Due in {{days}}d",
    "dueDate": "Due {{date}}",
    "loading": "Loading utility accounts…",
    "noAccounts": "No utility accounts configured for this apartment.",
    "noAccountsHint": "Add them via Admin → Properties → Utility Accounts.",
    "type": {
      "electricity": "Electricity",
      "gas": "Gas",
      "water": "Water",
      "internet": "Internet",
      "maintenance": "Maintenance",
      "insurance": "Insurance",
      "tax": "Tax",
      "other": "Other"
    },
    "status": {
      "cancelled": "Cancelled",
      "paid": "Paid",
      "overdueDays": "Overdue {{days}}d",
      "graceDays": "Grace {{days}}d left",
      "dueInDays": "Due in {{days}}d"
    },
    "summary": {
      "allTime": "all time invoices",
      "gracePeriod": "Grace period",
      "inGraceWindow": "still in grace window",
      "invoiceCount": "{{count}} invoices",
      "overdue": "Overdue",
      "pastDue": "invoices past due",
      "pendingLabel": "Pending (RON)",
      "total": "Total"
    },
    "table": {
      "amount": "Amount",
      "provider": "Provider / Period",
      "status": "Status",
      "type": "Type"
    },
    "breakdown": {
      "invoiceCount": "{{count}} invoice(s)",
      "overdueCount": "{{count}} overdue",
      "pending": "pending",
      "title": "Breakdown by apartment"
    },
    "accounts": {
      "addAccount": "+ Add account via Admin",
      "addViaAdmin": "Add one via Admin",
      "noneConfigured": "No utility accounts configured.",
      "pullHistory": "Pull history",
      "sync": "Sync",
      "title": "Utility accounts"
    },
    "readings": {
      "noReadings": "No readings yet",
      "sent": "Sent",
      "submittedToProvider": "Submitted to provider",
      "title": "Readings"
    },
    "action": {
      "markPaid": "Mark as paid",
      "paidCheck": "Paid ✓",
      "viewPdf": "View PDF"
    },
    "loadFailed": "Failed to load utility data. Please refresh.",
    "loadingBills": "Loading utility bills…",
    "monthlyHistory": "Monthly cost history",
    "noHistoryData": "No invoice data yet. Pull history to populate.",
    "noInvoices": "No invoices.",
    "noInvoicesForType": "No invoices for {{type}}."
  },
  "rentDue": {
    "nth1st": "1st",
    "nth2nd": "2nd",
    "nth3rd": "3rd",
    "nth4th": "4th",
    "nthLast": "Last",
    "monday": "Monday",
    "tuesday": "Tuesday",
    "wednesday": "Wednesday",
    "thursday": "Thursday",
    "friday": "Friday",
    "saturday": "Saturday",
    "sunday": "Sunday",
    "dayOfMonth": "Day of month",
    "nthWeekday": "Nth weekday",
    "dayHelper": "Day of month (1–28)"
  },
  "apartmentDetail": {
    "activeMaintenanceCount": "{{count}} active maintenance entries for {{building}}.",
    "addEntry": "Add Entry",
    "apartmentDetails": "Apartment Details",
    "bathrooms": "Bathrooms",
    "building": "Building",
    "buildingMaintenanceInProgress": "Building maintenance in progress",
    "confirmConvertLegacyTenant": "Convert this legacy tenant to a standard Identity entity?\n\nThis will:\n• Create a new Person entity with the current data\n• Assign them as Tenant for this apartment\n• Remove the legacy tenant entry",
    "confirmDelete": "Delete \"{{name}}\"? This cannot be undone.",
    "confirmDeleteLegacyTenant": "Permanently delete this legacy tenant record?",
    "confirmRemoveLegacyTenant": "Remove legacy tenant \"{{name}}\"?",
    "confirmRemoveRole": "Remove this role assignment?",
    "contractEnd": "Contract End",
    "contractType": "Contract Type",
    "convertToEntity": "Convert to Entity",
    "convertToEntityTooltip": "Create an Identity entity from this legacy tenant and assign as Tenant",
    "deleteApartment": "Delete apartment",
    "deposit": "Deposit",
    "documentsWillAppear": "Documents assigned to this apartment will appear here.",
    "dueDate": "due {{date}}",
    "editLegacyTenant": "Edit legacy tenant",
    "editLegacyTenantTitle": "Edit Legacy Tenant",
    "editRoles": "Edit roles",
    "email": "Email",
    "firstName": "First Name",
    "floor": "Floor",
    "floorLabel": "Floor",
    "furnished": "Furnished",
    "goToDocuments": "Go to Documents",
    "lastName": "Last Name",
    "linkedPeople": "Linked People",
    "monthlyRent": "Monthly Rent",
    "moreItems": "+ {{count}} more",
    "moveInDate": "Move-in Date",
    "moveOutDate": "Move-out Date",
    "newTaskEntry": "New Task / Maintenance Entry",
    "noDocumentsLinked": "No documents linked",
    "noLinkedPeople": "No linked people",
    "noMaintenanceEntries": "No maintenance entries for this apartment",
    "noTasks": "No tasks for this apartment",
    "notes": "Notes",
    "notesLabel": "Notes",
    "parking": "Parking",
    "permanentAddress": "Permanent Address",
    "phone": "Phone",
    "removeLegacyTenant": "Remove legacy tenant",
    "rentalStart": "Rental Start",
    "rentDueDay": "Rent Due Day",
    "rooms": "Rooms",
    "scheduledMaintenanceCount": "{{count}} scheduled maintenance entries upcoming for {{building}}.",
    "secondaryPhone": "Secondary Phone",
    "statMaintenanceBudget": "Maintenance Budget",
    "statMonthlyRent": "Monthly Rent",
    "statOpenMaintenance": "Open Maintenance",
    "statStatus": "Status",
    "statusLabel": "Status",
    "storage": "Storage",
    "surface": "Surface",
    "tenantLegacy": "tenant (legacy)",
    "theBuilding": "the building",
    "totalCount": "{{count}} total",
    "upcomingBuildingMaintenance": "Upcoming building maintenance",
    "viewBuildingMaintenance": "View building maintenance →"
  },
  "finances": {
    "addManualHolding": "Add manual holding",
    "addManually": "Add manually",
    "all": "All",
    "apiBearerToken": "API bearer token",
    "autoInterest": "Auto (interest)",
    "avgCost": "Avg Cost",
    "binanceCsv": "Binance Trade History CSV",
    "binanceDesc": "Export from Binance: Orders → Trade History → Export. Net crypto holdings are computed from your buy/sell history.",
    "chooseBinanceFile": "Choose Binance file",
    "chooseEtoroFile": "Choose eToro statement (.xlsx / .csv)",
    "chooseRevolutFile": "Choose Revolut file",
    "chooseTradevilleFile": "Choose Tradeville file",
    "computed": "computed",
    "confirmDelete": "Delete {{symbol}}?",
    "currency": "Currency",
    "currentPrice": "Current Price",
    "deposits": "Deposits",
    "displayName": "Display name",
    "editHolding": "Edit — {{symbol}}",
    "etoroApiKeyPlaceholder": "x-api-key (Public API Key)",
    "etoroKeyMgmt": "in eToro → Settings → Trading → API Key Management.",
    "etoroLiveSync": "eToro Live Sync",
    "etoroLiveSyncDesc": "Syncs your live portfolio directly from the eToro API.",
    "etoroStatement": "eToro Account Statement",
    "etoroStatementDesc": "Download from eToro: Portfolio → History → Account Statement → Download. Use the full date range. Upload the .xlsx or .csv file.",
    "etoroUserKeyPlaceholder": "x-user-key (User Key)",
    "family": "Family",
    "familyValue": "Family value",
    "fetchingAuthToken": "Fetching auth token from server…",
    "generateKeys": "Generate keys",
    "ghostfolioDesc": "Push holdings from the active portfolio view to your Ghostfolio instance as MANUAL BUY activities.",
    "ghostfolioSync": "Ghostfolio sync",
    "ghostfolioTokenPlaceholder": "Ghostfolio Security Token",
    "ghostfolioUrl": "Ghostfolio URL",
    "holdingsAcrossPortfolios": "{{count}} holdings across {{portfolios}} portfolios",
    "holdingsCount": "{{count}} holdings",
    "importErrors": "Import errors",
    "importSync": "Import / Sync",
    "importTitle": "Import — {{name}}",
    "interestAccrual": "Interest accrual (auto-computes current value)",
    "isin": "ISIN",
    "isinPlaceholder": "e.g. RO9P01XXG005",
    "maturity": "Maturity",
    "moveToAnotherPortfolio": "Move to another portfolio",
    "name": "Name",
    "noHoldingsYet": "No holdings yet. Import a CSV or sync from eToro.",
    "noPortfoliosDesc": "Add a portfolio via the Django admin or API, then import your holdings.",
    "noPortfoliosYet": "No portfolios yet",
    "notes": "Notes",
    "openGhostfolio": "Open Ghostfolio",
    "personal": "Personal",
    "personalValue": "Personal value",
    "plPct": "P/L %",
    "price": "Price",
    "prices": "Prices",
    "profitLossPct": "Profit/Loss %",
    "profitLossTab": "Profit/Loss ({{tab}})",
    "qty": "Qty",
    "quantityRequired": "Quantity *",
    "ratePerYear": "Rate % / year",
    "ratePlaceholder": "e.g. 7.5",
    "recomputeDepositsTitle": "Recompute deposit/bond accrued values",
    "refreshPricesTitle": "Refresh market prices via Yahoo Finance",
    "rememberInBrowser": "Remember in browser",
    "rememberKeys": "Remember keys in this browser (localStorage)",
    "revolutCsv": "Revolut Stocks CSV",
    "revolutDesc": "Transaction history from Revolut (Profile → Statements → Revolut Stocks). Net positions are computed automatically.",
    "selectPortfolio": "Select portfolio…",
    "source": "Source",
    "startDate": "Start date",
    "symbol": "Symbol",
    "symbolFilterPlaceholder": "Symbol filter (comma-separated, optional)",
    "symbolPlaceholder": "e.g. TVBETETF",
    "symbolRequired": "Symbol *",
    "syncFromEtoroApi": "Sync from eToro API",
    "syncToGhostfolio": "Sync {{count}} portfolio(s) to Ghostfolio",
    "totalValue": "Total value",
    "tradevilleCsv": "Tradeville CSV",
    "tradevilleDesc": "Snapshot CSV from Tradeville. Use \"Symbol filter\" to import only specific symbols (e.g. TVBETETF for Personal, R3202A for Family).",
    "type": "Type",
    "value": "Value"
  },
  "apartment": {
    "notFound": "Apartment not found"
  },
  "knowledge": {
    "title": "Knowledge",
    "subtitle": "Family and personal second brain",
    "familyBrain": "Family brain",
    "myBrain": "My brain",
    "searchNotes": "Search notes…",
    "newNote": "New note",
    "noNotes": "No notes yet",
    "selectOrCreate": "Select a note or create a new one",
    "noteTitle": "Note title",
    "markdownPlaceholder": "Write markdown…",
    "tags": "Tags",
    "addTag": "Add tag…",
    "backlinks": "Backlinks",
    "noBacklinks": "No linked entities yet",
    "daily": "daily",
    "edit": "Edit",
    "preview": "Preview",
    "emptyBody": "Empty note",
    "unsaved": "Unsaved changes",
    "untitled": "Untitled",
    "noteCreated": "Note created",
    "noteSaved": "Note saved",
    "noteDeleted": "Note deleted",
    "saveError": "Failed to save note",
    "deleteError": "Failed to delete note",
    "deleteConfirm": "Delete this note permanently?",
    "familyReadOnly": "Family brain notes are read-only for non-admins",
    "openTodayDaily": "Open today's daily note",
    "createTodayDaily": "Create today's daily note",
    "searchAll": "Search notes and documents…",
    "searchMode": {
      "text": "Text",
      "semantic": "Semantic",
      "hybrid": "Hybrid"
    },
    "noSearchResults": "No results",
    "hitDocument": "doc",
    "hitNote": "note",
    "searchSettings": "Search settings",
    "searchSettingsSaved": "Search settings saved",
    "searchSettingsError": "Failed to save search settings",
    "includeDocuments": "Include documents",
    "includeDocumentsHelp": "Search family documents in unified results",
    "includeCareer": "Include career notes",
    "includeCareerHelp": "Include personal career-ops notes in unified search"
  }
}
